import React from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Loader2, CheckCircle } from 'lucide-react';
import { PasswordInput } from '../components/PasswordInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [pwd, setPwd] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(false);

  function submit(e) {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) { setError(t('resetPw.min8')); return; }
    if (pwd !== confirm) { setError(t('resetPw.mismatch')); return; }
    setLoading(true);
    axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pwd })
      .then(() => setDone(true))
      .catch((err) => setError((err.response && err.response.data && err.response.data.detail) || t('resetPw.genericError')))
      .finally(() => setLoading(false));
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20 px-4">
      <div className="w-full max-w-md bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
        {done ? (
          <div className="text-center">
            <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">{t('resetPw.doneTitle')}</h1>
            <p className="text-zinc-400 text-sm">{t('resetPw.doneDesc')}</p>
            <button onClick={() => navigate('/login')} className="mt-6 rounded-full bg-[#F9004D] hover:bg-[#D00040] text-white font-bold px-6 py-3">
              {t('resetPw.login')}
            </button>
          </div>
        ) : !token ? (
          <div className="text-center">
            <h1 className="text-xl font-bold text-white mb-2">{t('resetPw.invalidTitle')}</h1>
            <p className="text-zinc-400 text-sm">{t('resetPw.invalidDesc')}</p>
            <button onClick={() => navigate('/forgot-password')} className="mt-6 rounded-full bg-[#F9004D] hover:bg-[#D00040] text-white font-bold px-6 py-3">
              {t('resetPw.newRequest')}
            </button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>{t('resetPw.title')}</h1>
            <p className="text-zinc-400 text-sm mb-6">{t('resetPw.subtitle')}</p>
            <form onSubmit={submit} className="space-y-4">
              <PasswordInput
                leftIcon={<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />}
                value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={t('resetPw.newPwPlaceholder')}
                required minLength={8}
                className="bg-black/20 border-white/10 text-white py-3.5"
                data-testid="reset-password" />
              <PasswordInput
                leftIcon={<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t('resetPw.confirmPlaceholder')}
                required minLength={8}
                className="bg-black/20 border-white/10 text-white py-3.5"
                data-testid="reset-confirm" />
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">{error}</div>}
              <button type="submit" disabled={loading}
                className="w-full rounded-full bg-[#F9004D] hover:bg-[#D00040] text-white font-bold py-3.5 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reset-submit">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('resetPw.submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
